import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import Classes from '../common/Classes';
import { Helmet } from 'react-helmet';
import { VIDYARD_CONFIG } from '../common/Constants';
import { trackMixpanelEvent } from '../common/MixpanelHelper';
import mixpanelConstants from '../common/mixpanelConstants';


const ProductDetailsVideo = ({ videoId, productId, productName, exhibitorId, exhibitorName, packageName }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const isFullyInViewport = useRef(true);
  const shouldResumeOnViewportEnter = useRef(true);

  useEffect(() => {
    const attachPauseListener = (player) => {
      if (!player) return;

      player.on('play', () => {
        if (player.uuid === videoId) {
          shouldResumeOnViewportEnter.current = true;
        }
      });
      
      player.on('pause', () => {
        // Only track if video is fully in viewport
        if (!isFullyInViewport.current) return;
        
        if (player.uuid === videoId) {
          shouldResumeOnViewportEnter.current = false;

          const currentTime = player._previousTime || 0;
          const totalDuration = player.metadata?.length_in_seconds || 0;
          
          if (Math.floor(currentTime) >= 3) {
            const videoMixpanelProps = {
              component: mixpanelConstants.component.productDetails,
              productName: productName,
              productId: productId,
              exhibitorId,
              exhibitorName,
              packageName,
              subComponent: mixpanelConstants.subComponent.standard,
              videoId: videoId,
              playDuration: Math.floor(currentTime),
              actionOnVideo: mixpanelConstants.propertyValue.actionOnVideo.videoPaused,
              totalVideoDuration: Math.floor(totalDuration),
              percentageWatched: totalDuration > 0 ? Math.floor((currentTime / totalDuration) * 100) : 0
            };
            trackMixpanelEvent(mixpanelConstants.eventName.productVideoPlayed, videoMixpanelProps);
          }
        }
      });
    };
    
    window.onVidyardAPI = (vidyardEmbed) => {
      vidyardEmbed.api.addReadyListener((_, player) => {
        playerRef.current = player;
        attachPauseListener(player);

        if (isFullyInViewport.current && shouldResumeOnViewportEnter.current) {
          player.play();
          return;
        }

        player.pause();
      });
    };
    
    let observer;
    if (containerRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          isFullyInViewport.current = entry.intersectionRatio >= 1;

          const videoPlayer = playerRef.current || window.VidyardV4?.players?.find(p => p?.uuid === videoId);

          if (videoPlayer) {
            playerRef.current = videoPlayer;

            if (isFullyInViewport.current) {
              if (shouldResumeOnViewportEnter.current) {
                videoPlayer.play();
              }
            } else {
              videoPlayer.pause();
            }
          }
          
          if (entry.intersectionRatio < 1) {
            if (window.VidyardV4 && window.VidyardV4.players) {
              if (videoPlayer && videoPlayer._previousTime) {
                const currentTime = videoPlayer._previousTime || 0;
                const totalDuration = videoPlayer.metadata?.length_in_seconds || 0;

                if (Math.floor(currentTime) >= 3) {
                  const videoMixpanelProps = {
                    component: mixpanelConstants.component.productDetails,
                    productName: productName,
                    productId: productId,
                    exhibitorId,
                    exhibitorName,
                    packageName,
                    subComponent: mixpanelConstants.subComponent.standard,
                    videoId: videoId,
                    playDuration: Math.floor(currentTime),
                    actionOnVideo: mixpanelConstants.propertyValue.actionOnVideo.leftViewport,
                    totalVideoDuration: Math.floor(totalDuration),
                    percentageWatched: totalDuration > 0 ? Math.floor((currentTime / totalDuration) * 100) : 0
                  };
                  trackMixpanelEvent(mixpanelConstants.eventName.productVideoPlayed, videoMixpanelProps);
                }
              }
            }
          }
        },
        { threshold: 1.0 }
      );

      observer.observe(containerRef.current);
    }
    
    return () => {
      if (observer) observer.disconnect();
    };
  }, [videoId, productId, productName]);

  return (<div className={Classes.productImageContainer} ref={containerRef}>
    <Helmet>
      <script src={VIDYARD_CONFIG.playerScriptUrl} type='text/javascript' />
    </Helmet>
    <MediaQuery minWidth={1199}>
      <img
        className={'vidyard-player-embed'}
        data-uuid={videoId}
        data-width='1160'
        data-height='600'
        data-v='4'
        data-type='inline'
        data-muted='1'
        data-autoplay='1'></img>
    </MediaQuery>
    <MediaQuery minWidth={992} maxWidth={1198}>
      <img
        className={'vidyard-player-embed'}
        data-uuid={videoId}
        data-width='1160'
        data-height='600'
        data-v='4'
        data-type='inline'
        data-muted='1'
        data-autoplay='1'></img>
    </MediaQuery>
    <MediaQuery maxWidth={991}>
      <img
        className={'vidyard-player-embed'}
        data-uuid={videoId}
        data-width='991'
        data-height='300'
        data-v='4'
        data-type='inline'
        data-muted='1'
        data-autoplay='1'></img>
    </MediaQuery>
  </div>)
};


ProductDetailsVideo.propTypes = {
  videoId: PropTypes.string,
  productId: PropTypes.string,
  productName: PropTypes.string,
  exhibitorId: PropTypes.string,
  exhibitorName: PropTypes.string,
  packageName: PropTypes.string
};

export default ProductDetailsVideo;
